import { ScenarioData } from "@/types/plans";
import { BusinessGoal, Plan, Project, Scenario, UUID } from "@/types/project";
import { normalizePercentAllocations } from "@/utils/budgetNormalization";

/**
 * ProjectService centralizes project-scoped utilities. It is instantiated with a
 * single project object and then reused as a lightweight read-only façade. This
 * keeps traversal logic (business goals → plans → scenarios) in one place and
 * prevents it from being duplicated across views.
 *
 * Responsibilities:
 * - expose convenience accessors for fetching scenarios, plans, and business
 *   goals by their identifiers without re-implementing nested loops
 * - provide domain-specific helpers such as "pickPlanScenarioFilter" that
 *   encapsulate fallback logic for selecting the most relevant scenario
 * - share utilities for preparing scenario payloads before persisting them via
 *   the API (see prepareScenarioForSave)
 */

type ScenarioChannelSelectionForSave = {
  channel_name: string;
  percentage: number;
  amount: number;
};

export type ScenarioCampaignKpiForSave = {
  kpi_name: string;
  percentage: number | null;
  amount: number | null;
  kpi_goal: string;
  kpi_goal_amount: number;
  channel_selections: ScenarioChannelSelectionForSave[];
  channel_exclusions: string[];
  skip_channel_prediction: boolean;
};

export type ScenarioForSave = {
  id: string;
  name: string;
  owned_by?: string | null;
  owner?: string | null;
  approved: boolean;
  dma: string[];
  states: string[];
  cities: string[];
  zip_codes: string[];
  gender: string;
  age_group: string;
  start_date: string | null;
  end_date: string | null;
  estimated_audience_size: string | null;
  kpi_goal_enabled: boolean;
  campaign_kpis: ScenarioCampaignKpiForSave[];
  net_budget: number | null;
  gross_budget: number | null;
};

export interface ScenarioPreparationOptions {
  scenario: ScenarioData;
  ownerName?: string | null;
  ownerEmail?: string | null;
  scenarioId?: string;
  approved?: boolean;
  marketsToDma?: (markets: string[]) => string[];
  marketsToStates?: (markets: string[]) => string[];
}

const defaultMarketsToDma = (markets: string[] = []): string[] =>
  markets.map((market) => market.split("__")[1]).filter(Boolean);

const defaultMarketsToStates = (markets: string[] = []): string[] =>
  markets.map((market) => market.split("__")[0]).filter(Boolean);

export class ProjectService {
  private project: Project;

  /**
   * Cache the project snapshot. The instance can then be reused as a
   * read-through object without re-fetching or cloning the project tree.
   */
  constructor(project: Project) {
    this.project = project;
  }

  /**
   * Traverse every business goal and plan to locate the first scenario that
   * matches the provided identifier. Returns undefined when the scenario is not
   * present, allowing callers to handle missing data gracefully.
   */
  getScenarioById(scenarioId: string): Scenario | undefined {
    for (const bg of this.project.business_goals ?? []) {
      for (const plan of bg.plans ?? []) {
        const scenario = (plan.scenarios ?? []).find((s) => s.id === scenarioId);
        if (scenario) {
          return scenario;
        }
      }
    }
    return undefined;
  }

  /**
   * Provides direct access to a plan by its id without requiring the caller to
   * loop through business goals. Useful for UI handlers that hold only plan
   * metadata after user interaction.
   */
  getPlanById(planId: string): Plan | undefined {
    for (const bg of this.project.business_goals ?? []) {
      const plan = (bg.plans ?? []).find((p) => p.id === planId);
      if (plan) {
        return plan;
      }
    }
    return undefined;
  }

  /**
   * Fetch a business goal by its id from the cached project. Returns undefined
   * when the id is not found, which allows the caller to defer to alternate
   * fallbacks.
   */
  getBusinessGoalById(businessGoalId: UUID): BusinessGoal | undefined {
    return (this.project.business_goals ?? []).find((bg) => bg.id === businessGoalId);
  }

  /**
   * Identifies the plan that owns a given scenario. This is helpful when
   * external code only knows the scenario id (e.g., from table filters) yet
   * needs to mutate plan-level data.
   */
  getPlanByScenarioId(scenarioId: string): Plan | undefined {
    for (const bg of this.project.business_goals ?? []) {
      for (const plan of bg.plans ?? []) {
        const scenario = (plan.scenarios ?? []).find((s) => s.id === scenarioId);
        if (scenario) {
          return plan;
        }
      }
    }
    return undefined;
  }

  /**
   * Determine which scenario id should be used as the active filter for a plan
   * by preferring approved scenarios and falling back to the first available
   * scenario. Returns undefined if the plan has no scenarios.
   */
  pickPlanScenarioFilter(planId: string): UUID | undefined {
    const plan = this.getPlanById(planId);
    if (!plan) return undefined;

    const scenarios = plan.scenarios ?? [];
    if (scenarios.length === 0) return undefined;

    const approvedScenario = scenarios.find((s) => s.approved === true);
    if (approvedScenario) {
      return approvedScenario.id;
    }

    return scenarios[0].id;
  }

  /**
   * Normalize a scenario payload before persisting it. Consumers can supply
   * overrides for ownership/meta fields or provide custom DMA/state splitters
   * when markets are preprocessed differently in a view.
   */
  static prepareScenarioForSave(options: ScenarioPreparationOptions): ScenarioForSave {
    const {
      scenario,
      ownerName,
      ownerEmail,
      scenarioId,
      approved,
      marketsToDma = defaultMarketsToDma,
      marketsToStates = defaultMarketsToStates,
    } = options;

    const id = scenarioId ?? scenario.id;
    if (!id) {
      throw new Error("Scenario id is required to prepare payload");
    }

    const isByGoal = scenario.kpiGoalEnabled === true;
    const isSingleKpi = (scenario.campaignKpis || []).length === 1;

    const toNumber = (value: unknown): number => (Number.isFinite(Number(value)) ? Number(value) : 0);

    const net =
      !isByGoal && Number.isFinite(Number(scenario.netBudget)) ? Math.round(Number(scenario.netBudget)) : null;
    const gross =
      !isByGoal && Number.isFinite(Number(scenario.grossBudget)) ? Math.round(Number(scenario.grossBudget)) : null;

    const dmaList =
      Array.isArray(scenario.dma) && scenario.dma.length ? scenario.dma : marketsToDma(scenario.markets || []);
    const stateList =
      Array.isArray(scenario.states) && scenario.states.length
        ? scenario.states
        : marketsToStates(scenario.markets || []);

    const campaignKpis = (scenario.campaignKpis || []).map((kpi) => {
      const kpiAmount = isByGoal
        ? null
        : isSingleKpi
        ? Number(net || 0)
        : toNumber(scenario.kpiNetBudgets?.[kpi]?.amount ?? 0);

      const selections: ScenarioChannelSelectionForSave[] = (scenario.kpiChannels?.[kpi] || []).map((channel) => ({
        channel_name: channel,
        percentage: toNumber(scenario.kpiChannelBudgets?.[kpi]?.[channel]?.percent ?? 0),
        amount: toNumber(scenario.kpiChannelBudgets?.[kpi]?.[channel]?.amount ?? 0),
      }));

      if (!isByGoal) {
        const normalizedChannels = normalizePercentAllocations(
          selections.map((sel) => ({ id: sel.channel_name, percent: sel.percentage ?? 0 })),
          { target: kpiAmount && kpiAmount > 0 ? 100 : 0 }
        );
        const channelPercentMap = new Map(normalizedChannels.normalized.map((item) => [item.id, item.percent]));
        selections.forEach((sel) => {
          const normalizedPercent = channelPercentMap.get(sel.channel_name);
          if (typeof normalizedPercent === "number") sel.percentage = normalizedPercent;
        });
      }

      const kpiPercent = isByGoal ? null : isSingleKpi ? 100 : toNumber(scenario.kpiNetBudgets?.[kpi]?.percent ?? 0);

      return {
        kpi_name: kpi,
        percentage: kpiPercent,
        amount: kpiAmount,
        kpi_goal: scenario.kpiGoals?.[kpi] ?? "",
        kpi_goal_amount: toNumber(scenario.kpiGoalValues?.[kpi] || 0),
        channel_selections: selections,
        channel_exclusions: scenario.kpiExclusions?.[kpi] || [],
        skip_channel_prediction: selections.length > 0 ? !!scenario.kpiSkipChannelPrediction?.[kpi] : false,
      } as ScenarioCampaignKpiForSave;
    });

    if (!isByGoal) {
      const normalizedKpis = normalizePercentAllocations(
        campaignKpis.map((item) => ({ id: item.kpi_name, percent: item.percentage ?? 0 })),
        { target: net && net > 0 ? 100 : 0 }
      );
      const kpiPercentMap = new Map(normalizedKpis.normalized.map((item) => [item.id, item.percent]));
      campaignKpis.forEach((item) => {
        const normalizedPercent = kpiPercentMap.get(item.kpi_name);
        if (typeof normalizedPercent === "number") item.percentage = normalizedPercent;
      });
    }

    return {
      id,
      name: scenario.name,
      owned_by: ownerName ?? undefined,
      owner: ownerEmail ?? undefined,
      approved: typeof approved === "boolean" ? approved : Boolean(scenario.approved),
      dma: dmaList,
      states: stateList,
      cities: Array.isArray(scenario.cities) ? scenario.cities : [],
      zip_codes: Array.isArray(scenario.zipCodes) ? scenario.zipCodes : [],
      gender: scenario.genders?.join(",") || "",
      age_group: scenario.ageGroups?.join(",") || "",
      start_date: scenario.startDate,
      end_date: scenario.endDate,
      estimated_audience_size: scenario.estimatedAudienceSize == null ? null : String(scenario.estimatedAudienceSize),
      kpi_goal_enabled: isByGoal,
      campaign_kpis: campaignKpis,
      net_budget: net,
      gross_budget: gross,
    };
  }
}
